/** The Week's panels (DESIGN §16.6): a day, a weights session, or a part of the rest of the plan, over the matrix. */
import type { ReactNode } from "react";
import { Chip, SidePanel, Status, Table, type Column } from "../../kit/index.js";
import { useModel } from "../context.js";
import { dayLong, isDay, weekRange } from "../model/days.js";
import { plural, planTitle } from "../model/plan.js";
import { athletesText, type CoachSession } from "../model/sessions.js";
import { norm } from "../model/plan.js";
import { setText } from "../model/sets.js";
import {
  cleanLines,
  columnName,
  columnsText,
  dayCountText,
  dayCounts,
  loggedBlocks,
  partName,
  sessionColumns,
  weekDays,
  type Athlete,
  type AthleteTrack,
  type WeekModel,
  type WeekTrack,
} from "../model/week.js";
import { href } from "../paths.js";
import { SheetLink } from "../SheetLink.js";
import { EffortMarks, LiftSections } from "./common.js";

function sessionMeta(model: WeekModel, cs: CoachSession): string {
  const cols = cs.teamSessionID ? sessionColumns(model, cs.id) : [];
  const who = cs.teamSessionID && !cs.given.length ? "no athletes in its columns yet" : athletesText(cs);
  return (cols.length ? cols.join(", ") + " · " : "") + who;
}

function theirTrack(a: Athlete, t: WeekTrack): AthleteTrack | undefined {
  return a.track.find((x) => x.key === t.key);
}

function trackMeta(t: WeekTrack): string {
  if (t.running) {
    const owed = t.athletes.filter((a) => theirTrack(a, t)?.running);
    const done = owed.filter((a) => theirTrack(a, t)!.done.length).length;
    return columnsText(t.columns) + " · " + (owed.length ? done + " of " + owed.length + " done" : "no athletes yet");
  }
  const logged = t.athletes.filter((a) => a.track.some((x) => x.key === t.key && x.logged)).map((a) => a.name);
  return (
    columnsText(t.columns) +
    " · " +
    (t.athletes.length ? plural(t.athletes.length, "athlete", "athletes") : "no athletes yet") +
    (logged.length ? " · logged by " + logged.join(", ") : "")
  );
}

function TrackLines({ t }: { t: WeekTrack }) {
  const lines = cleanLines(t.lines);
  if (!lines.length) return <p className="k-muted">The sheet gives no details for {t.label}.</p>;
  return (
    <ul className="wk-lines">
      {lines.map((l, i) => (
        <li key={i}>{l}</li>
      ))}
    </ul>
  );
}

function Missing({ model, title, text, before }: { model: WeekModel; title: string; text: string; before?: ReactNode }) {
  return (
    <SidePanel title={title} closeTo={href(model.monday)}>
      {before ?? <p className="k-muted">{text}</p>}
    </SidePanel>
  );
}

/** `before`: what opens the day ahead of its sessions, the day's meets (§27.3.1). */
export function DayPanel({ date, before }: { date: string; before?: ReactNode }) {
  const model = useModel();
  const day = weekDays(model).find((d) => d.date === date);
  if (!day) return <Missing model={model} title={isDay(date) ? dayLong(date) : "Day not found"} text="Nothing on the plan or shared that day." before={before} />;
  const c = dayCounts(day);
  const sheet = model.document?.week;
  return (
    <SidePanel
      title={dayLong(day.date) + (day.date === model.today ? " · Today" : "")}
      subtitle={day.items.map(partName).join(" · ") + (c.weights || c.running ? " · " + dayCountText(c) : "")}
      closeTo={href(model.monday)}
    >
      {before}
      {day.items.map((it, i) => {
        if (it.cs) {
          return (
            <section className="wk-part" key={i}>
              <div className="wk-part-head">
                <h3>{partName(it)}</h3>
                <span className="k-muted">{sessionMeta(model, it.cs)}</span>
              </div>
              <LiftSections cs={it.cs} level="h4" />
            </section>
          );
        }
        const t = it.track!;
        return (
          <section className="wk-part" key={i}>
            <div className="wk-part-head">
              <h3>
                <Chip label={t.label} />
              </h3>
              <span className="k-muted">{trackMeta(t)}</span>
            </div>
            <TrackLines t={t} />
            {t.running ? <RunResults t={t} /> : null}
          </section>
        );
      })}
      {sheet ? (
        <p>
          <SheetLink week={sheet} />
        </p>
      ) : null}
    </SidePanel>
  );
}

export function SessionPanel({ id }: { id: string }) {
  const model = useModel();
  const cs = model.coach.find((x) => x.id === id);
  if (!cs) return <Missing model={model} title="Session not found" text={"It is not in " + weekRange(model.monday) + "."} />;
  return (
    <SidePanel title={cs.plan ? planTitle(cs.plan) : "Workout"} subtitle={dayLong(cs.day) + " · " + sessionMeta(model, cs)} closeTo={href(model.monday)}>
      <LiftSections cs={cs} level="h3" />
      {cs.sheet ? (
        <p>
          <SheetLink week={cs.sheet.week} />
        </p>
      ) : null}
    </SidePanel>
  );
}

/** Each rep they logged from the run, by exercise: "Tempo 150 m · 21.4 s, 150 m · 22 s". */
/** `30 m ×3, 30 m · 4.4 s` and `2 done`: the same rep in a row is said once, with how many. */
export function repeated(reps: string[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < reps.length; ) {
    let n = 1;
    while (reps[i + n] === reps[i]) n++;
    out.push(n === 1 ? reps[i]! : reps[i] === "Done" ? `${n} done` : `${reps[i]} ×${n}`);
    i += n;
  }
  return out;
}

function RunReps({ track }: { track: AthleteTrack }) {
  const lines: { name: string; reps: string[] }[] = [];
  for (const { workout, block } of loggedBlocks(track)) {
    for (const e of block.exercises ?? []) {
      const reps = (workout.session.logs ?? [])
        .filter((l) => norm(l.exerciseID) === norm(e.id))
        .sort((x, y) => (x.setIndex ?? 0) - (y.setIndex ?? 0))
        .map((l) => setText(l, e));
      if (!reps.length) continue;
      const last = lines[lines.length - 1];
      if (last?.name === e.name) last.reps.push(...reps);
      else lines.push({ name: e.name, reps });
    }
  }
  return (
    <span className="wk-run">
      <Status kind="done" />
      {lines.map((l, i) => (
        <span key={i} className="wk-run-reps num">
          <b>{l.name}</b> {repeated(l.reps).join(", ")}
        </span>
      ))}
      {track.done.map((w, i) => (
        <EffortMarks key={"fx" + i} session={w.session} />
      ))}
    </span>
  );
}

function runResult(track: AthleteTrack | undefined, today: string) {
  if (!track?.running) return <span className="k-muted">—</span>;
  if (track.done.length) return <RunReps track={track} />;
  const past = track.date < today;
  const kind = past ? "missed" : track.date === today ? "today" : "planned";
  return <Status kind={kind} text={past ? "Not logged" : kind === "today" ? "Today, not done yet" : "Not done yet"} />;
}

/** A run's athletes: Done with their reps' times, or not done (§19.5). */
function RunResults({ t, caption }: { t: WeekTrack; caption?: string }) {
  const model = useModel();
  const columns: Column<Athlete>[] = [
    { key: "name", label: "Athlete", render: (a) => a.name },
    { key: "column", label: "Column", render: (a) => (a.column ? columnName(a.column) : <span className="k-muted">—</span>) },
    { key: "result", label: "Reps", wrap: true, render: (a) => runResult(theirTrack(a, t), model.today) },
  ];
  return (
    <Table
      rows={t.athletes}
      columns={columns}
      rowKey={(a) => a.id}
      href={(a) => href(model.monday, "athlete", a.id)}
      label={caption ?? t.label}
      empty={"No athlete is in " + columnsText(t.columns) + " yet."}
    />
  );
}

export function TrackPanel({ trackKey }: { trackKey: string }) {
  const model = useModel();
  const t = model.track.find((x) => x.key === trackKey);
  if (!t) return <Missing model={model} title="Session not found" text={"It is not in " + weekRange(model.monday) + "."} />;
  const columns: Column<Athlete>[] = [
    { key: "name", label: "Athlete", render: (a) => a.name },
    { key: "column", label: "Column", render: (a) => (a.column ? columnName(a.column) : <span className="k-muted">—</span>) },
    {
      key: "logged",
      label: "Logged",
      hideLabel: true,
      render: (a) => (theirTrack(a, t)?.logged ? <Status kind="logged" /> : null),
    },
  ];
  return (
    <SidePanel title={t.label} subtitle={dayLong(t.date) + " · " + (t.running ? trackMeta(t) : columnsText(t.columns))} closeTo={href(model.monday)}>
      <section>
        <h3>On the plan</h3>
        <TrackLines t={t} />
      </section>
      <section>
        <h3>Athletes</h3>
        {t.running ? (
          <>
            <RunResults t={t} caption={"Athletes with " + t.label} />
            <p className="wk-foot">A run is logged on the phone with Done; a time on each rep is optional.</p>
          </>
        ) : (
          <>
            <Table
              rows={t.athletes}
              columns={columns}
              rowKey={(a) => a.id}
              href={(a) => href(model.monday, "athlete", a.id)}
              label={"Athletes with " + t.label}
              empty={"No athlete is in " + columnsText(t.columns) + " yet."}
            />
            <p className="wk-foot">Athletes see this on the day with their workout. They are not asked to log it.</p>
          </>
        )}
      </section>
    </SidePanel>
  );
}
